import React, { useState, useEffect, Fragment } from "react";
import { Text, Image } from "@sitecore-jss/sitecore-jss-react";
import { useTranslation } from "react-i18next";

import { DATALAYER_EVENTS } from "@constants";
import { NavLink } from "react-router-dom";
import { log } from "@assets/js/utils";

import onMediaChanged from "@assets/js/on-media-changed";
import getViewportSize from "@assets/js/get-viewport-size";
import ViewportSizes from "@assets/js/viewport-sizes";
import MapMarkerSvg from "@assets/js/icons/map-marker";

import ContactLinksGroup from "../ContactLinksGroup";
import NewsletterSignUp from "../NewsletterSignUp";
import FooterLogo from "../FooterLogo";
import Collapsible from "../Collapsible";
import InputFieldwButton from "../forms/InputFieldwButton";
import useGlobalStore from "@store/global";
import shallow from "zustand/shallow";
import dataLayerPush from "@assets/js/gtm";
import path from "@assets/js/browser-path";
import { ChevronSvg } from "@assets/js/icons";

import "./site-footer.scss";

const SiteFooter = ({ fields = {}, rendering }) => {
  let currentViewport = getViewportSize();
  const { t } = useTranslation();
  
  const texts = {
    contactUs: t("FritzHansen_Project_ConsumersAndProfessionals_Footer_ContactUs"),
    followUs: t("FritzHansen_Project_ConsumersAndProfessionals_Footer_FollowUs"),
    stores: t("FritzHansen_Project_ConsumersAndProfessionals_Footer_Stores")
  }

  const { uid } = rendering;
  const initMobile =
    currentViewport?.name === ViewportSizes.SMALL.name ||
    currentViewport?.name === ViewportSizes.MEDIUM.name;

  const [setMobile] = useState(initMobile);
  const [expanded, setExpanded] = useState(false);

  const [menuItems, setMenuItems] = useState([]);

  useEffect(() => {

    let items = fields?.menuRoot?.fields?.Items[0]?.fields?.SubItems;

    if (items) {
      const newArray = items.map((item) => {
        item.expanded = false;
        return item;
      });
      setMenuItems(newArray);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleHandler = (id) => {
    let updatedList = menuItems.map((item) => {
      if (item.id === id) {
        item.expanded = true;
      } else {
        item.expanded = false;
      }
      return item;
    });
    setMenuItems(updatedList);
  };


  onMediaChanged((newViewport) => {
    switch (newViewport.name) {
      case ViewportSizes.SMALL.name:
      case ViewportSizes.MEDIUM.name:
        setMobile(true);
        setExpanded(false);

        break;
      case ViewportSizes.LARGE.name:
      case ViewportSizes.XLARGE.name:
      default:
        setMobile(false);
        setExpanded(true);
        break;
    }
  });
  onMediaChanged(currentViewport);

  let children = (
    <>
      <InputFieldwButton
        id="id"
        name="input"
        type="email"
        placeholder="Sign up"
        ariaDescribedBy="info success"
        iconType="arrow"
        errorMessage="error"
        successMessage="success"
        isInverted={true}
      />
    </>
  );

  const newsletter = {
    headline: fields.newsletterHeader,
    link: fields.newsletterLink,
    isInverted: false,
  };

  const menuItemClickHandler = (menuPoint, url) => {
    let obj = {
      event: DATALAYER_EVENTS.NAVIGATION,
      navigationType: "footer navigation",
      navigationMenuPoint: menuPoint,
      navigationDestination: url
    }

    log("siteFooter.menuItemClickHandler", obj);
    // Tracking
    dataLayerPush(obj);
  }

  return (
    <>
      {fields && (
        <>
          <div className="site-footer__upper-part">
            <div className="site-footer__newsletter-signup">
              <NewsletterSignUp fields={newsletter} children={children} />
            </div>
            <div className="site-footer__some">
              <h4 className="site-footer__headline">{texts.followUs}</h4>
              <div className="site-footer__some-icons">
                {fields?.Logos && fields.Logos.map((logo, index) => {
                  if(logo?.fields?.Logo?.value?.src) {
                    return <div key={`Footer-SoMe-icon${index}`} className="site-footer__some-icons__item">
                      {logo?.fields?.Link?.value?.href ? 
                        <a href={logo.fields.Link.value.href} target="_blank">
                          <img src={logo.fields.Logo.value.src} />
                        </a> 
                        :
                        <img src={logo.fields.Logo.value.src} />
                      }
                    </div>
                  }
                  return <Fragment key={`Footer-SoMe-icon${index}`}></Fragment>;
                })}
              </div>
            </div>
            {fields?.phone?.value && (
              <div className="site-footer__contact">
                <h4 className="site-footer__headline">{texts.contactUs}</h4>
                <ContactLinksGroup
                  phone={fields?.phone}
                  email={fields?.email}
                  email2={fields?.email2}
                />
              </div>
            )}
            {fields?.findStoreLink?.value?.text && (
              <div className="site-footer__stores">
                <h4 className="site-footer__headline">{texts.stores}</h4>
                <div className="site-footer__store-locator">
                  <MapMarkerSvg className="site-footer__map-marker-icon" />
                  <NavLink to={fields.findStoreLink.value.href} onClick={() => {
                    let url = fields.findStoreLink.value.href;
                    if (url && !url.startsWith('http')) {
                      url = path.join(process.env.REACT_APP_ROOTAPI, url);
                    }
                    menuItemClickHandler(fields?.findStoreLink?.value?.text, url);
                  }}>
                    <span>{fields?.findStoreLink?.value?.text}</span>
                  </NavLink>
                </div>
              </div>
            )}
          </div>
          <footer className="site-footer" key={uid}>
            <div className="site-footer__lower-part">
              {/* =================== NAVIGATION =================== */}
              <div className="site-footer__nav-list--mobile">
                {menuItems?.map((topic, index) => {
                  return (
                    <Collapsible
                      key={`navlistmobilemenuitem${index}`}
                      toggleHandler={() => toggleHandler(topic.id)}
                      id={topic.id}
                      preExpanded={topic.expanded}
                      triggerComponent={
                        <>
                          <Text tag="h2" field={topic?.fields?.Title} />
                          <ChevronSvg isInverted={true} />
                        </>
                      }
                      contentComponent={
                        <FooterMobileNavList
                          key={"menuitem23423" + index}
                          headline={topic?.fields?.Title}
                          topics={topic?.fields?.SubItems}
                          clickHandler={menuItemClickHandler}
                        />
                      }
                    />
                  );
                })}
              </div>
              <div className="site-footer__nav-list">
                {menuItems?.map((topic, index) => {
                  return (
                      <FooterNavList
                        key={"menuitem23423" + index}
                        headline={topic?.fields?.Title}
                        topics={topic?.fields?.SubItems}
                        clickHandler={menuItemClickHandler}
                      />
                  );
                })}
              </div>
              {/* =================== NAVIGATION END =================== */}
              <div className="site-footer__logo-wrapper">
                <div className="site-footer__footer-logo">
                  <FooterLogo />
                </div>
                <div className="site-footer__footer-logo">
                  <Image field={fields.bCorpLogo} />
                </div>
              </div>
            </div>
          </footer>
        </>
      )}
    </>
  );
};
export default SiteFooter;

const FooterMobileNavList = ({ topics, headline, clickHandler }) => {
  return topics && (
    <ul>
      {topics.map((child, index) => {
        return (
          <li key={`mobilefooterLi${index}`}>
            <p className="site-footer__collapsible-item">
              <a href={child?.fields?.Link?.value?.href} target={child?.fields?.Link?.value?.target} onClick={() => {
                let url = child?.fields?.Link?.value?.href;
                if (url && !url.startsWith('http')) {
                  url = path.join(process.env.REACT_APP_ROOTAPI, url);
                }
                clickHandler(`${headline?.value} > ${child?.fields?.Title?.value}`, url);
              }}>
                {child?.fields?.Title?.value}
              </a>
            </p>
          </li>
        );
      })}
    </ul>
  )
}

const FooterNavList = ({ headline, topics, clickHandler }) => {
  return (
    <nav className="site-footer__link-item-group">
      {headline && (
        <Text field={headline} tag='h4' className="site-footer__headline" />
      )}
      {topics && (
        <ul>
          {topics.map((child, index) => {
            return (
              <li key={`footerLi${index}`}>
                <p className="site-footer__collapsible-item">
                  <a href={child?.fields?.Link?.value?.href} target={child?.fields?.Link?.value?.target} onClick={() => {
                    let url = child?.fields?.Link?.value?.href;
                    if (url && !url.startsWith('http')) {
                      url = path.join(process.env.REACT_APP_ROOTAPI, url);
                    }
                    clickHandler(`${headline?.value} > ${child?.fields?.Title?.value}`, url);
                  }}>
                    {child?.fields?.Title?.value}
                  </a>
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </nav>
  );
};
